/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useLocation, useNavigate } from 'js/lib/useRouter';

import { Grid, Pagination, breakpoints, useTheme } from '@coursera/cds-core';
import type { Theme } from '@coursera/cds-core';
import { ArrowNextIcon } from '@coursera/cds-icons';

import type { Maybe } from 'bundles/ent-website/__generated__/baseContentfulTypes';
import type {
  EntWebsiteBlockResourceHubQuery,
  EntWebsiteResourceCardFragment,
  EntWebsiteResourceHubFragment,
} from 'bundles/ent-website/__generated__/contentfulTypes';
import ResourceHubCard from 'bundles/ent-website/components/ResourceHubCard';
// FIXME: existing import/no-cycle violations are excused to prevent seeing errors when modifying other parts of the same file; please fix it carefully
// eslint-disable-next-line import/no-cycle
import BlockLayout from 'bundles/ent-website/contentful/core/BlockLayout';
import Button from 'bundles/ent-website/contentful/core/ContentfulButton';
import Headline from 'bundles/ent-website/contentful/modules/shared/Headline';
import { entWebsiteBlockResourceHubQuery } from 'bundles/ent-website/queries/EntWebsiteResourceHub';
import type { ResourceHubCardItem } from 'bundles/ent-website/types';
import { genResourceHubPages } from 'bundles/ent-website/utils/resourceHubUtils';
import type { BlockModuleInnerProps } from 'bundles/ent-website/utils/withBlockQuery';
import withBlockQuery from 'bundles/ent-website/utils/withBlockQuery';
import EnterpriseWebsiteExperiments from 'bundles/epic/clients/EnterpriseWebsite';

type Props = BlockModuleInnerProps<EntWebsiteResourceHubFragment>;

const ITEMS_PER_PAGE = 9;

const styles = {
  headlineWrapper: css`
    display: flex;
    margin-bottom: var(--cds-spacing-400);

    ${breakpoints.down('xs')} {
      flex-direction: column;
    }

    h2 {
      margin-bottom: 0;
    }

    a {
      margin-left: var(--cds-spacing-300);

      ${breakpoints.down('xs')} {
        margin-left: 0;
        margin-top: 12px;

        span {
          display: flex;
          justify-content: start;
        }
      }
    }
  `,
  resourcesWrapper: (_: Theme, enablePagination: boolean) => css`
    gap: var(--cds-spacing-400);
    row-gap: var(--cds-spacing-800);
    display: grid;
    grid-template-columns: repeat(${enablePagination ? 3 : 4}, 1fr);

    ${breakpoints.down('sm')} {
      grid-template-columns: repeat(2, 1fr);
    }

    ${breakpoints.down('xs')} {
      grid-template-columns: repeat(1, 1fr);
    }
  `,
};

export function ResourceHub({ content, assembly }: Props) {
  const rootElRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const [pageNumber, setPageNumber] = useState<number>(Number(location?.query?.pageNumber ?? 1));

  const { headline, cta, resourceCardsCollection, usePagination } = content;

  const enablePagination = useMemo(
    () => usePagination && EnterpriseWebsiteExperiments?.get('resourceHubPaginationKillswitch'),
    [usePagination]
  );

  const pages: Array<Maybe<{ __typename?: 'EntWebsiteResourceCard' } & EntWebsiteResourceCardFragment>>[] = useMemo(
    () => genResourceHubPages(resourceCardsCollection?.items ?? []),
    [resourceCardsCollection?.items]
  );

  // Scrolling to top of the pagination results once page change triggered by user
  const scrollToPageStart = useCallback<() => $TSFixMe>(() => {
    setTimeout(() => {
      rootElRef?.current?.scrollIntoView?.();
    }, 10);
  }, []);

  const onPageChange = useCallback<(...args: $TSFixMe[]) => $TSFixMe>(
    (_e, idx) => {
      // // Having page number in query for URL sharing and SEO
      const newParams = new URLSearchParams(location?.query);
      newParams.set('pageNumber', idx);
      navigate({ search: `?${newParams?.toString()}`, pathname: location?.pathname });
      setPageNumber(idx);
      scrollToPageStart();
    },
    [location?.pathname, location?.query, navigate, scrollToPageStart]
  );

  useEffect(() => {
    const listener = () => {
      const newParams = new URLSearchParams(window?.location?.search);
      const queryPageNumber = Number(newParams.get('pageNumber') ?? 1);
      setPageNumber(queryPageNumber);
      scrollToPageStart();
    };

    // Supporting browser native navigation
    window.addEventListener('popstate', listener);
    return () => {
      window.removeEventListener('popstate', listener);
    };
  }, [scrollToPageStart]);

  const contentPanel = () => (
    <div
      ref={rootElRef}
      css={css`
        scroll-margin-top: 150px;
      `}
    >
      <div css={styles.headlineWrapper}>
        <Headline headline={headline} visible={assembly.visibility.headline} />
        {assembly.visibility.headline && (
          <Button
            component="a"
            variant="ghost"
            href={cta?.ctaTarget}
            module={content}
            position={0}
            icon={<ArrowNextIcon />}
          >
            {cta?.ctaText}
          </Button>
        )}
      </div>
      <div css={styles.resourcesWrapper(theme, enablePagination ?? false)}>
        {(enablePagination ? pages[pageNumber - 1] : resourceCardsCollection?.items)?.map(
          (item: ResourceHubCardItem) => (
            <ResourceHubCard key={item?.slug} item={item} />
          )
        )}
      </div>
      {(resourceCardsCollection?.items?.length ?? 0) > 9 && enablePagination === true ? (
        <Grid
          container
          justifyContent="center"
          alignItems="center"
          css={css`
            margin-top: 48px;
          `}
        >
          <Pagination
            total={resourceCardsCollection?.items?.length ?? 0}
            pageSize={ITEMS_PER_PAGE}
            page={pageNumber}
            hidePageSizeInput
            onPageChange={onPageChange}
          />
        </Grid>
      ) : null}
    </div>
  );

  const imagePanel = () => null;

  return (
    <BlockLayout
      assembly={assembly}
      renderContent={contentPanel}
      renderImage={imagePanel}
      blockStyle={css`
        padding-top: 32px;
      `}
    />
  );
}

// @ts-expect-error TSMIGRATION-5.0 TS2344
export const BlockResourceHub = withBlockQuery<EntWebsiteResourceHubFragment, EntWebsiteBlockResourceHubQuery>(
  entWebsiteBlockResourceHubQuery
)(ResourceHub);
